"use client";

import { cn } from "@/lib/utils";
import type { Participant } from "../base-match-template";
import {
  ContactShadow,
  FieldMatchFrame,
  type FieldMatchState,
  FieldScore,
  FieldScoreDetail,
  FloatingProp,
  Floodlight,
  PerspectivePlane,
  type PlaneSpec,
  projectOnPlane,
  StadiumStands,
} from "./field-sport-scene-kit";

export type BasketballCourtPreviewProps = {
  yourTeam: Participant[];
  opponentTeam: Participant[];
  yourWon?: boolean;
  matchScore?: { yourPoints: number; opponentPoints: number };
  periods?: Array<{ label: string; yourScore: number; opponentScore: number }>;
  linkToProfiles?: boolean;
};

/** Pebbled orange leather with black channels. */
const Basketball = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "relative overflow-hidden rounded-full bg-[radial-gradient(circle_at_34%_28%,#ffb067_0%,#e86a22_45%,#9c3a0c_100%)]",
      className,
    )}
    style={{
      boxShadow:
        "inset -2px -3px 5px rgba(60,16,0,0.55), inset 1px 2px 3px rgba(255,214,170,0.55)",
    }}
  >
    <div className="absolute inset-0 [background-image:radial-gradient(rgba(70,20,0,0.5)_0.5px,transparent_0.8px)] [background-size:2px_2px] opacity-40" />
    <div className="absolute top-0 bottom-0 left-1/2 w-px -translate-x-1/2 bg-[#2a0d02]/85" />
    <div className="absolute top-1/2 right-0 left-0 h-px -translate-y-1/2 bg-[#2a0d02]/85" />
    <div className="absolute top-[-12%] bottom-[-12%] left-[-58%] w-[80%] rounded-[100%] border border-[#2a0d02]/85" />
    <div className="absolute top-[-12%] right-[-58%] bottom-[-12%] w-[80%] rounded-[100%] border border-[#2a0d02]/85" />
    <div className="absolute top-[10%] left-[20%] h-[30%] w-[34%] rounded-full bg-white/35 blur-[1px]" />
  </div>
);

const LINE = "border-white/80";

const COURT: PlaneSpec = {
  top: 66,
  depth: 178,
  tilt: 60,
  perspective: 300,
  farWidth: 112,
};
const CENTRE = projectOnPlane(COURT, 0.5, 78);

/** Hardwood court, drawn flat in plane coordinates. */
function CourtFloor() {
  return (
    <>
      {/* Maple: warm base, plank rows along the length, staggered joints */}
      <div className="absolute inset-0 bg-[linear-gradient(90deg,#b8773d_0%,#d29a58_30%,#dca664_50%,#d29a58_70%,#b8773d_100%)]" />
      <div className="absolute inset-0 bg-[repeating-linear-gradient(180deg,rgba(90,44,10,0.0)_0px,rgba(90,44,10,0.0)_4.5px,rgba(90,44,10,0.28)_4.5px,rgba(90,44,10,0.28)_5px)]" />
      <div className="absolute inset-0 bg-[repeating-linear-gradient(180deg,rgba(255,226,180,0.1)_0px,rgba(255,226,180,0.1)_5px,transparent_5px,transparent_10px,rgba(120,60,20,0.08)_10px,rgba(120,60,20,0.08)_15px,transparent_15px,transparent_25px)]" />
      <div className="absolute inset-0 [background-image:repeating-linear-gradient(90deg,transparent_0px,transparent_47px,rgba(90,44,10,0.32)_47px,rgba(90,44,10,0.32)_48px),repeating-linear-gradient(90deg,transparent_0px,transparent_23px,rgba(90,44,10,0.26)_23px,rgba(90,44,10,0.26)_24px)] [background-size:100%_10px,100%_10px] [background-position:0_0,11px_5px] [background-repeat:repeat] opacity-35" />

      {/* Court boundary, half-court line */}
      <div
        className={cn(
          "absolute top-[10px] right-[4%] bottom-[30px] left-[4%] border-2",
          LINE,
        )}
      />
      <div className="absolute top-[10px] bottom-[30px] left-1/2 w-[2px] -translate-x-1/2 bg-white/80" />

      {/* Centre circle with a painted club disc */}
      <div className="absolute top-[78px] left-1/2 h-[62px] w-[62px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white/80 bg-[radial-gradient(circle,#1d2c58_0%,#1d2c58_55%,transparent_57%)]" />
      <div className="absolute top-[78px] left-1/2 h-[22px] w-[22px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#f2b45b]/90" />

      {/* Each end: painted key, free-throw circle, three-point arc */}
      {(["left", "right"] as const).map((side) => (
        <div
          key={side}
          className={cn(
            "absolute top-[10px] bottom-[30px] w-[40%] overflow-hidden",
            side === "left" ? "left-[4%]" : "right-[4%]",
          )}
        >
          <div
            className={cn(
              "absolute top-1/2 h-[46px] w-[44%] -translate-y-1/2 border-2 bg-[#1d2c58]/90",
              LINE,
              side === "left" ? "left-0 border-l-0" : "right-0 border-r-0",
            )}
          />
          <div
            className={cn(
              "absolute top-1/2 h-[46px] w-[46px] -translate-y-1/2 rounded-full border-2",
              LINE,
              side === "left"
                ? "left-[44%] -translate-x-1/2"
                : "right-[44%] translate-x-1/2",
            )}
          />
          <div
            className={cn(
              "absolute top-1/2 h-[118px] w-[118px] -translate-y-1/2 rounded-full border-2",
              LINE,
              side === "left"
                ? "left-[10%] -translate-x-1/2"
                : "right-[10%] translate-x-1/2",
            )}
          />
        </div>
      ))}
    </>
  );
}

function BasketballArena() {
  return (
    <>
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#080404_0%,#1a0d06_100%)]" />

      <StadiumStands
        className="h-[68px]"
        crowdA="255,190,120"
        crowdB="120,160,255"
        crowdOpacity={0.7}
        baseClassName="bg-[linear-gradient(180deg,#070404_0%,#1a0f0a_100%)]"
        boardClassName="bg-[linear-gradient(90deg,#3d1606_0%,#ff8a2a_20%,#ffe2bd_35%,#ff8a2a_50%,#3d1606_65%,#ff8a2a_82%,#ffe2bd_100%)] opacity-80 shadow-[0_0_14px_rgba(255,138,42,0.6)]"
      />

      <PerspectivePlane plane={COURT}>
        <CourtFloor />
      </PerspectivePlane>

      {/* Arena lights and their reflections in the lacquer */}
      <Floodlight
        side="left"
        x="7%"
        y={8}
        beamTo="30%"
        color="255,230,200"
        beamOpacity={0.13}
      />
      <Floodlight
        side="right"
        x="7%"
        y={8}
        beamTo="30%"
        color="255,230,200"
        beamOpacity={0.13}
      />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_7%_38%_at_22%_68%,rgba(255,244,224,0.32),transparent),radial-gradient(ellipse_7%_38%_at_78%_68%,rgba(255,244,224,0.32),transparent),radial-gradient(ellipse_40%_30%_at_50%_60%,rgba(255,230,190,0.16),transparent)]" />
      <div className="absolute inset-x-0 top-[60px] h-[24px] bg-[linear-gradient(180deg,transparent,rgba(255,214,160,0.16)_50%,transparent)]" />

      {/* Depth */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_60%,rgba(30,10,0,0.45)_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_95%_at_50%_45%,transparent_55%,rgba(0,0,0,0.5)_100%)]" />
    </>
  );
}

export function BasketballCourtPreview({
  yourTeam,
  opponentTeam,
  yourWon,
  linkToProfiles = false,
  matchScore,
  periods,
}: BasketballCourtPreviewProps) {
  const opponentWon = opponentTeam.length > 0 && yourWon === false;
  const yourIsWinner = !!yourWon && !opponentWon;

  const matchState: FieldMatchState =
    yourTeam.length === 0 && opponentTeam.length === 0
      ? "EMPTY"
      : matchScore &&
          (matchScore.yourPoints > 0 ||
            matchScore.opponentPoints > 0 ||
            (periods && periods.length > 0))
        ? "FULL"
        : "NO_SCORE";

  const totalPoints = matchScore
    ? matchScore.yourPoints + matchScore.opponentPoints
    : 0;
  const shownPeriods = (periods ?? []).slice(-4);

  return (
    <FieldMatchFrame
      label="Basketball"
      frameClassName="bg-[#120804]"
      matchState={matchState}
      scene={<BasketballArena />}
      yourTeam={yourTeam}
      opponentTeam={opponentTeam}
      yourWinner={yourIsWinner}
      opponentWinner={opponentWon}
      linkToProfiles={linkToProfiles}
      bandClassName="top-[50px] bottom-1"
      decor={
        matchState !== "NO_SCORE" ? null : (
          // Tip-off: the ball hangs over the centre circle.
          <div
            className="absolute -translate-x-1/2 -translate-y-full"
            style={{ left: CENTRE.left, top: CENTRE.top - 6 }}
          >
            <ContactShadow className="-bottom-[10px] left-1/2 h-[7px] w-[24px] -translate-x-1/2" />
            <FloatingProp amplitude={4} duration={3.6}>
              <Basketball className="h-[22px] w-[22px] @sm:h-[26px] @sm:w-[26px]" />
            </FloatingProp>
          </div>
        )
      }
      center={
        matchState === "FULL" && matchScore ? (
          <>
            <FieldScore
              transitionKey={totalPoints}
              left={matchScore.yourPoints}
              right={matchScore.opponentPoints}
              leftWinner={yourIsWinner}
              rightWinner={opponentWon}
              valueClassName={
                Math.max(matchScore.yourPoints, matchScore.opponentPoints) >=
                100
                  ? "text-[36px] @xs:text-[46px] @sm:text-[60px] @md:text-[72px]"
                  : "text-[46px] @xs:text-[56px] @sm:text-[70px] @md:text-[80px]"
              }
            />
            {shownPeriods.length > 0 ? (
              <FieldScoreDetail className="max-w-[178px] @sm:max-w-none">
                {shownPeriods.map((period, i) => (
                  <span
                    key={`${period.label}-${period.yourScore}-${period.opponentScore}`}
                    className="flex items-center gap-1 whitespace-nowrap"
                  >
                    {i > 0 ? (
                      <span className="hidden text-white/35 @sm:inline">·</span>
                    ) : null}
                    <span className="text-white/50">{period.label}</span>
                    <span
                      className={cn(
                        period.yourScore !== period.opponentScore
                          ? "text-white/95"
                          : "text-white/70",
                      )}
                    >
                      {period.yourScore}-{period.opponentScore}
                    </span>
                  </span>
                ))}
              </FieldScoreDetail>
            ) : null}
          </>
        ) : null
      }
    />
  );
}
